import React, { useCallback, useEffect, useRef, useState } from 'react';

export default React.memo(function QuizResultDisplay({
	score,
	questions,
	timedOut,
	selectedAnswers,
	isAnswerCorrect,
	onRetakeQuiz,
	MathTextComponent
}) {
	const scoreRatio = questions.length > 0 ? (score / questions.length) : 0;
	const scoreOnTen = scoreRatio * 10;
	const passed = scoreOnTen >= 5;
	console.log("giá trị của passed:", passed);
	const tenScaleScore = scoreOnTen.toFixed(1);

	let feedbackMessage = 'Chưa đạt, ôn tập lại kiến thức cơ bản và cùng luyện tập lại nhé';
	let feedbackIcon = 'fas fa-seedling text-success';
	if (scoreOnTen >= 9) {
    feedbackMessage = 'Xuất sắc! Bạn đã nắm vững kiến thức, luyện tập thêm để rút ngắn thời gian làm bài nhé';
    feedbackIcon = 'fas fa-trophy text-warning'; // cúp vàng - đỉnh cao thành tích
	} else if (scoreOnTen >= 7) {
		feedbackMessage = 'Gần đạt rồi, đừng quên luyện tập thêm để tránh gặp phải những lỗi sai nhé';
		feedbackIcon = 'fas fa-star-half-alt text-warning'; // gần đạt - sao nửa vời
	} else if (scoreOnTen >= 5) {
		feedbackMessage = 'Cố gắng lên, hãy bổ sung thêm kiến thức nền tảng và đừng quên luyện tập lại nhé';
		// feedbackIcon = 'fas fa-dumbbell text-success'; // đang trên đường tiến bộ
		// feedbackIcon = 'fas fa-shoe-prints text-success'; // đang trên đường tiến bộ
		// feedbackIcon = 'fas fa-fire text-success'; // đang trên đường tiến bộ
		feedbackIcon = 'fas fa-chart-line text-success'; // đang trên đường tiến bộ
	}
	const summaryHeroIcon = `${feedbackIcon} fa-3x mb-3`;
	const [isSummaryCollapsed, setIsSummaryCollapsed] = useState(false);
	const panelRef = useRef(null);
	const dragStateRef = useRef({ active: false, offsetX: 0, offsetY: 0 });
	const [panelPosition, setPanelPosition] = useState({ x: null, y: 78 });

	const clampPosition = useCallback((x, y) => {
		const panel = panelRef.current;
		if (!panel) {
			return { x, y };
		}

		const maxX = Math.max(8, window.innerWidth - panel.offsetWidth - 8);
		const maxY = Math.max(8, window.innerHeight - panel.offsetHeight - 8);

		return {
			x: Math.min(Math.max(8, x), maxX),
			y: Math.min(Math.max(8, y), maxY)
		};
	}, []);

	const handleDragStart = useCallback((event) => {
		if (window.innerWidth <= 992) {
			return;
		}

		const panel = panelRef.current;
		if (!panel) {
			return;
		}

		const rect = panel.getBoundingClientRect();
		dragStateRef.current = {
			active: true,
			offsetX: event.clientX - rect.left,
			offsetY: event.clientY - rect.top
		};

		document.body.style.userSelect = 'none';
	}, []);

	const handleDragMove = useCallback((event) => {
		if (!dragStateRef.current.active || window.innerWidth <= 992) {
			return;
		}

		const nextX = event.clientX - dragStateRef.current.offsetX;
		const nextY = event.clientY - dragStateRef.current.offsetY;
		setPanelPosition(clampPosition(nextX, nextY));
	}, [clampPosition]);

	const handleDragEnd = useCallback(() => {
		if (!dragStateRef.current.active) {
			return;
		}

		dragStateRef.current.active = false;
		document.body.style.userSelect = '';
	}, []);

	useEffect(() => {
		window.addEventListener('mousemove', handleDragMove);
		window.addEventListener('mouseup', handleDragEnd);

		return () => {
			window.removeEventListener('mousemove', handleDragMove);
			window.removeEventListener('mouseup', handleDragEnd);
			document.body.style.userSelect = '';
		};
	}, [handleDragEnd, handleDragMove]);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth <= 992 || panelPosition.x === null) {
				return;
			}

			setPanelPosition((prev) => clampPosition(prev.x, prev.y));
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [clampPosition, panelPosition.x]);

	const panelStyle = panelPosition.x === null
		? { top: `${panelPosition.y}px`, right: '16px', left: 'auto' }
		: { top: `${panelPosition.y}px`, left: `${panelPosition.x}px`, right: 'auto' };

	return (
		<>
			<div className={`quiz-result-layout ${isSummaryCollapsed ? 'is-summary-collapsed' : ''}`}>
				<aside
					ref={panelRef}
					className={`quiz-result-summary-panel ${isSummaryCollapsed ? 'is-collapsed' : ''}`}
					style={panelStyle}
				>
					<div className="quiz-result-summary-toolbar" onMouseDown={handleDragStart}>
						<h3 className="mb-0">Kết quả bài thi</h3>
						<button
							type="button"
							className="btn btn-sm btn-outline-secondary"
							onMouseDown={(event) => event.stopPropagation()}
							onClick={() => setIsSummaryCollapsed((prev) => !prev)}
						>
							<i className={`fas ${isSummaryCollapsed ? 'fa-chevron-left' : 'fa-chevron-right'} me-1`}></i>
							{isSummaryCollapsed ? 'Mở rộng' : 'Thu nhỏ'}
						</button>
					</div>

					{!isSummaryCollapsed && (
						<div className="quiz-result-summary-content text-center">
							<i className={summaryHeroIcon}></i>

							{timedOut && (
								<div className="alert alert-warning text-start text-md-center">
									<i className="fas fa-hourglass-end me-2"></i>
									Đã hết thời gian làm bài. Hệ thống đã tự nộp bài và hiển thị kết quả.
								</div>
							)}

							<div className="mb-1 p-4 bg-light rounded">
								<h1 className={passed ? 'text-success' : 'text-danger'}>
									{score}/{questions.length}
								</h1>
								<p className="lead mb-0">
									Điểm số: <strong>{tenScaleScore}/10</strong>
								</p>
								<p className="text-muted mb-0">
									<i className={`${feedbackIcon} me-2`} aria-hidden="true"></i>
									{feedbackMessage}
								</p>
							</div>

							<div className="text-center mt-1">
								<button
									className="btn btn-primary btn-lg"
									onClick={onRetakeQuiz}
								>
									<i className="fas fa-redo me-2"></i>
									Làm lại bài thi
								</button>
							</div>
						</div>
					)}
				</aside>

				<div className="text-start quiz-result-details">
					<h5 className="mb-3">
						<i className="fas fa-list-check me-2"></i>
						Chi tiết đáp án:
					</h5>

					{questions.map((q, qIndex) => {
						const selectedAnswer = selectedAnswers[q.id];
						const isCorrect = isAnswerCorrect(q, selectedAnswer);

						let selectedLetters = [];
						let userTextAnswer = '';

						if (q.questionType === 'text') {
							userTextAnswer = selectedAnswer || '';
						} else if (q.questionType === 'multiple') {
							if (selectedAnswer instanceof Set) {
								selectedLetters = Array.from(selectedAnswer)
									.map((idx) => String.fromCharCode(65 + idx))
									.sort();
							}
						} else if (selectedAnswer !== undefined) {
							selectedLetters = [String.fromCharCode(65 + selectedAnswer)];
						}

						return (
							<div key={q.id} className={`card mb-3 border-${isCorrect ? 'success' : 'danger'}`}>
								<div className="card-body">
									<div className="d-flex justify-content-between align-items-start">
										<div className="flex-grow-1">
											<div className="d-flex align-items-start mb-1 gap-1 flex-wrap quiz-question-heading">
												<span className="q-label flex-shrink-0">Câu {qIndex + 1}.</span>
												<div className="mb-0 quiz-question-text flex-grow-1" style={{ minWidth: 0 }}>
													<MathTextComponent text={q.question} />
												</div>
												{q.questionType === 'text' && (
													<span className="badge bg-secondary ms-2">
														<i className="fas fa-keyboard me-1"></i>
														Text Input
													</span>
												)}
												{q.questionType === 'multiple' && (
													<span className="badge bg-warning text-dark ms-2">
														<i className="fas fa-check-double me-1"></i>
														Multiple
													</span>
												)}
											</div>

											{q.questionType === 'text' ? (
												<div className="mb-2">
													<div
														className={`p-3 rounded ${
															isCorrect
																? 'bg-success bg-opacity-25 border border-success'
																: 'bg-danger bg-opacity-25 border border-danger'
														}`}
													>
														<strong>Câu trả lời của bạn:</strong>{' '}
														{userTextAnswer ? (
															<strong className="text-opt">{userTextAnswer}</strong>
														) : (
															<strong className="text-opt">Bạn Không trả lời</strong>
														)}{' '}
														{isCorrect ? (
															<i className="fas fa-check text-success ms-2"></i>
														) : (
															<i className="fas fa-times text-danger ms-2"></i>
														)}
													</div>

													<small className="text-success d-block mt-2">
														<strong>
															<i className="fas fa-check-circle me-1"></i>
															Đáp án chấp nhận:
														</strong>{' '}
														{q.correctAnswers.map((ans, idx) => (
															<span key={idx}>
																<code className="text-success">{ans}</code>
																{idx < q.correctAnswers.length - 1 && ', '}
															</span>
														))}
													</small>
												</div>
											) : (
												<>
													<div className="mb-2">
														{q.options.map((option, idx) => {
															const letter = String.fromCharCode(65 + idx);
															const isUserAnswer = selectedLetters.includes(letter);
															const isCorrectAnswer = q.correctAnswers.includes(letter);

															return (
																<div
																	key={idx}
																	className={`p-2 mb-1 rounded ${
																		isCorrectAnswer
																			? 'bg-success bg-opacity-25 border border-success'
																			: isUserAnswer
																			? 'bg-danger bg-opacity-25 border border-danger'
																			: ''
																	}`}
																>
																	<strong>{letter}.</strong>{' '}
																	<MathTextComponent text={option} />
																	{isCorrectAnswer && <i className="fas fa-check text-success ms-2"></i>}
																	{isUserAnswer && !isCorrectAnswer && (
																		<i className="fas fa-times text-danger ms-2"></i>
																	)}
																</div>
															);
														})}
													</div>

													<div className="mb-2">
														<small className="text-muted">
															<strong>Bạn đã chọn:</strong>{' '}
															{selectedLetters.length > 0
																? selectedLetters.join(', ')
																: <em className="text-danger">Không chọn đáp án</em>}
														</small>
														<br />
														<small className="text-success">
															<strong>Đáp án đúng:</strong> {q.correctAnswers.join(', ')}
														</small>
													</div>
												</>
											)}

											{q.explanation && (
												<div className="alert alert-info mb-0 mt-2">
													<small>
														<strong>
															<i className="fas fa-lightbulb me-1"></i>
															Giải thích:
														</strong>{' '}
														<MathTextComponent text={q.explanation} />
													</small>
												</div>
											)}
										</div>

										<div className="ms-3">
											{isCorrect ? (
												<i className="fas fa-check-circle text-success fa-2x"></i>
											) : (
												<i className="fas fa-times-circle text-danger fa-2x"></i>
											)}
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</>
	);
});
